import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  ArrowRight, 
  MapPin, 
  Calendar, 
  Users, 
  Trophy,
  Star,
  Plane,
  Bot,
  ChevronRight,
  ShoppingBag,
  GraduationCap,
  Award,
  Crown,
  Flag,
  Heart
} from 'lucide-react';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LandingPage = () => {
  const [stats, setStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes, spotlightRes] = await Promise.all([
          axios.get(`${API_URL}/stats/platform`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/events/upcoming?limit=4`).catch(() => ({ data: { events: [] } })),
          axios.get(`${API_URL}/spotlight/course`).catch(() => ({ data: { course: null } }))
        ]);
        setStats(statsRes.data);
        setUpcomingEvents(eventsRes.data?.events || []);
        setSpotlight(spotlightRes.data?.course || null);
      } catch (error) {
        console.error('Failed to fetch landing data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: MapPin, title: 'The Clubhouse', description: 'Discover courses, leagues, and connect with minority golf organizations nationwide.' },
    { icon: Calendar, title: 'The Itinerary', description: 'Every major tournament and local scramble in one view with iCal sync.' },
    { icon: Users, title: 'Connect & Network', description: 'Join city networks, chat with fellow golfers, and build lasting connections.' },
    { icon: Trophy, title: 'Live Scoring', description: 'Track rounds in real-time with hole-by-hole scoring and leaderboards.' },
    { icon: ShoppingBag, title: 'The Pro Shop', description: 'Support minority-owned golf brands with exclusive member discounts.' },
    { icon: GraduationCap, title: 'NextGen Junior Alliance', description: 'Connect junior golfers to HBCU golf programs and scholarships.' },
    { icon: Award, title: 'Directory', description: 'Find certified coaches, pros, and businesses for instruction and mentorship.' },
    { icon: Plane, title: 'Away Games', description: "Visiting a new city? Find local golfers who want to play." },
    { icon: CaddieHat, title: 'Caddie', description: 'Your AI golf concierge — find courses, book tee times, and get personalized recommendations.' }
  ];

  const quickLinks = [
    { icon: Flag, label: 'Find a Game', href: '/travel-guides', color: 'bg-green-50 text-green-700 hover:bg-green-100' },
    { icon: Heart, label: 'Volunteer', href: '/calendar', color: 'bg-pink-50 text-pink-700 hover:bg-pink-100' },
    { icon: Crown, label: 'Member Perks', href: '/membership', color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
    { icon: ShoppingBag, label: 'The Pro Shop', href: '/marketplace', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
  ];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/282eedd0-ae8d-412f-a647-081fded79209/images/ea4262fc246a86456962b5903a6d97eeca6da1dd413976d3cd39a0d3755de5ba.png)'
          }}
        >
          <div className="absolute inset-0 gradient-hero" />
        </div>
        
        <div className="relative z-10 container-padding w-full py-32 md:py-40">
          <div className="max-w-3xl">
            <p className="text-gold font-body font-semibold tracking-widest uppercase text-sm mb-4 animate-fade-in">
              One Game. One Network. One Fairway.
            </p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6 animate-slide-up">
              The National Network for{' '}
              <span className="text-gold">Minority Golfers</span>
            </h1>
            <p className="text-cream/90 text-lg md:text-xl font-body leading-relaxed mb-8 max-w-2xl animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Connect with minority golf organizations, leagues, tournaments, and courses across the United States. 
              Discover opportunities, build relationships, and grow the game together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/register">
                <Button className="btn-gold text-base px-8 py-6" data-testid="hero-join-btn">
                  Join the Alliance
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/explore">
                <Button 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 text-base px-8 py-6"
                  data-testid="hero-explore-btn"
                >
                  Explore Directory
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-gold rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-deep-green py-16 md:py-20">
        <div className="container-padding">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12">
            {[
              { value: stats?.courses || 0, label: 'Golf Courses' },
              { value: stats?.minority_courses || 0, label: 'Minority-Owned' },
              { value: stats?.events || 0, label: 'Events' },
              { value: stats?.leagues || 0, label: 'Leagues' },
              { value: stats?.golfers || 0, label: 'Members' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-display font-bold text-4xl md:text-5xl text-gold mb-2">
                  {stat.value.toLocaleString()}+
                </p>
                <p className="text-cream/70 font-body text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-10 bg-white border-b">
        <div className="container-padding">
          <div className="flex flex-wrap justify-center gap-4">
            {quickLinks.map((link, i) => (
              <Link key={i} to={link.href}>
                <button className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all ${link.color}`} data-testid={`quick-link-${i}`}>
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-bone">
        <div className="container-padding">
          <div className="text-center mb-16">
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-3">Platform Features</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-deep-green mb-4">
              Everything You Need to Connect
            </h2>
            <p className="text-muted-foreground font-body max-w-2xl mx-auto">
              From discovering courses to tracking your scores, Fairway Alliance provides 
              all the tools you need to engage with the minority golf community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className="card-premium hover-lift border-0 bg-white"
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-fairway rounded-sm flex items-center justify-center mb-6">
                    <feature.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-display font-semibold text-xl text-deep-green mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground font-body">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-3">Our Community</p>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-deep-green mb-6">
                Building a More Inclusive Future for Golf
              </h2>
              <p className="text-muted-foreground font-body text-lg leading-relaxed mb-8">
                Fairway Alliance bridges technology, community, and the game of golf. 
                We're creating valuable connections between players, organizations, and sponsors 
                to expand access, visibility, and opportunity within the sport.
              </p>
              <div className="space-y-4">
                {[
                  'Connect with golfers in your city and across the country',
                  'Discover minority-owned courses and support Black-owned businesses',
                  'Join leagues and participate in tournaments',
                  'Find sponsors and partnership opportunities'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                      <Star className="w-3 h-3 text-gold" />
                    </div>
                    <p className="text-foreground font-body">{item}</p>
                  </div>
                ))}
              </div>
              <Link to="/register" className="inline-block mt-8">
                <Button className="btn-gold" data-testid="community-join-btn">
                  Join Our Community
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/282eedd0-ae8d-412f-a647-081fded79209/images/e29c0233e2918bad0237ffa1bf4e58f4395d11fec319ef6fae04654a0af443dd.png"
                alt="Diverse golfers on fairway"
                className="rounded-sm shadow-card-hover w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 bg-fairway p-6 rounded-sm shadow-lg max-w-xs hidden md:block">
                <p className="text-gold font-display font-semibold text-lg mb-1">Nationwide Network</p>
                <p className="text-cream/80 font-body text-sm">
                  Connecting golfers across 50 states with shared passion and purpose.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 md:py-32 bg-bone">
          <div className="container-padding">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div>
                <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-3">Events Calendar</p>
                <h2 className="font-display font-bold text-3xl md:text-4xl text-deep-green">
                  Upcoming Events
                </h2>
              </div>
              <Link to="/events" className="mt-4 md:mt-0">
                <Button variant="outline" className="border-fairway text-fairway hover:bg-fairway hover:text-white">
                  View All Events
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {upcomingEvents.map((event, i) => (
                <Link 
                  key={event.event_id} 
                  to={`/events/${event.event_id}`}
                  className="block"
                >
                  <Card className="card-premium hover-lift border-0 bg-white h-full" data-testid={`event-card-${i}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-wider mb-3">
                        <Calendar className="w-4 h-4" />
                        {new Date(event.start_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                      <h3 className="font-display font-semibold text-lg text-deep-green mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <MapPin className="w-4 h-4" />
                        {event.city}, {event.state}
                      </div>
                      {event.entry_fee !== null && (
                        <p className="mt-3 text-gold font-semibold">
                          {event.entry_fee === 0 ? 'Free' : `$${event.entry_fee}`}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Course of the Month Spotlight */}
      {spotlight && (
        <section className="py-20 md:py-28 bg-white">
          <div className="container-padding">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <Badge className="bg-gold/20 text-gold mb-4">Course of the Month</Badge>
                <h2 className="font-display font-bold text-3xl md:text-4xl text-deep-green mb-4">
                  {spotlight.name}
                </h2>
                <p className="text-muted-foreground flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gold" />
                  {spotlight.city}, {spotlight.state}
                </p>
                {spotlight.minority_owned && (
                  <div className="mb-4">
                    <Badge className="bg-gold/20 text-gold">Black-Owned</Badge>
                  </div>
                )}
                <p className="text-foreground font-body text-lg leading-relaxed mb-6">
                  {spotlight.pricing_notes}
                </p>
                {spotlight.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {spotlight.amenities.map(a => (
                      <Badge key={a} variant="outline" className="border-fairway/30">{a}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-4">
                  <Link to={`/courses/${spotlight.course_id || spotlight.id}`}>
                    <Button className="btn-gold" data-testid="spotlight-view-btn">
                      View Course <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/explore">
                    <Button variant="outline" className="border-fairway text-fairway hover:bg-fairway hover:text-white">
                      Browse All Courses
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img
                  src={spotlight.images?.[0] || 'https://static.prod-images.emergentagent.com/jobs/282eedd0-ae8d-412f-a647-081fded79209/images/6f011343092baa1a9654a73bc6634fba69678ce9369b1fcca1c558e04417006c.png'}
                  alt={spotlight.name}
                  className="rounded-sm shadow-card-hover w-full object-cover aspect-[4/3]"
                />
                <div className="absolute -bottom-4 -right-4 bg-gold p-4 rounded-sm shadow-lg hidden md:block">
                  <Star className="w-8 h-8 text-deep-green" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-fairway">
        <div className="container-padding text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            Ready to Join the Alliance?
          </h2>
          <p className="text-cream/80 font-body text-lg max-w-2xl mx-auto mb-10">
            Create your free profile today and connect with thousands of golfers, 
            courses, and events across the nation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="btn-gold text-base px-10 py-6" data-testid="cta-join-btn">
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/explore">
              <Button 
                variant="outline" 
                className="border-2 border-cream/30 text-cream hover:bg-cream/10 text-base px-10 py-6"
              >
                Explore First
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
